import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import produce from 'immer';
import { Button } from '@saleshandy/design-system';
import { withTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import EmailInput from '../../../../../../../../unified-inbox/sub-components/email-input';
import EmailDropdownInput from './email-dropdown-input';
import Editor from '../../../../../../../../../shared/editor';
import { validate as rawValidate } from '../../../../../../../../sequence/shared/modals/email-modal/validator';
import {
  AttachmentType,
  AttachmentUploadStatus,
} from '../../../../../../../../../shared/editor/types';
import SingleEditorInput from '../../../../../../../../sequence/shared/modals/email-modal/components/single-editor-input';
import {
  prepareMergeTagOptions,
  attachmentStatusChangeHandler,
} from '../../../../../../../../sequence/shared/modals/email-modal/helpers';
import { RequestStatus } from '../../../../../../../../../shared/enums/request-status';
import {
  ReplyDraftPayload,
  UnifiedScheduleAttachment,
} from '../../../../../../../../unified-inbox/type';
import { Images } from '../../../../../../../../../shared/app-constants';
import DiscardEmailChangesModal from './discard-email-changes-modal';
import {
  accessibleOnClick,
  executeOnRequestStatus,
  getIsRequestPending,
  generateRandomString,
} from '../../../../../../../../../shared/utils';
import {
  CustomReplyForwardEditorProps,
  StateErrors,
  StateValues,
} from './types';
import {
  isValidHTML,
  nl2br,
} from '../../../../../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-merge-tag-mapping-with-values';
import {
  insertChipForMergeTag,
  insertChipForDateTag,
  setCaretAfterChip,
  dateChipTags,
  stripChipTagsFromHtml,
} from '../../../../../../../../../shared/editor/helpers';
import TemplateModal from '../../../../../../../../sequence/shared/modals/template-modal';
import { getFormattedAttachmentsData } from '../../../../../../../../sequence/shared/modals/variant-modal/helper';
import toaster, { Theme } from '../../../../../../../../../shared/toaster';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../../../../shared/design-system/components/overlay';

const CustomReplyForwardEditor: React.FC<CustomReplyForwardEditorProps> = ({
  label,
  action,
  selectedEmail,
  sequenceId,
  onSend,
  onDiscard,
  onFromFieldChange,
  t = (x: string) => x,
  className = '',
  inlineEditorInputValue,
  setInlineEditorInputValue,
  mergeTagOptions,
  editorDefaultFields,
  fromAddresses = [],
  draftProspectEmailRequest,
  draftProspectEmailRequestStatus,
  selectedProspectId,
  sendReplyDraftDiscardRequest,
  defaultDraftEditorFields,

  uploadAttachmentRequestStatus,
  uploadAttachmentRequestError,
  uploadAttachmentRequestMessage,
  uploadedAttachment,
  deleteAttachmentRequestStatus,
  deleteAttachmentRequestError,
  deletedAttachmentId,
  handleSendAttachmentUploadRequest,
  handleSendDeleteAttachmentRequest,
  handleResetUploadAttachmentRequest,
  handleResetDeleteAttachmentRequest,
  setAttachments,
  attachments,
  getEmailAccounts,
  emailAccount,
  showFromField = true,
  resetEmailDraftDataRequestStatus,
  resetReplyForwardDraftRequests,
  sendEmailProspectTabStatus,
  replyEmailRequestStatus,
  setSelectedRepliedEmailId,
  handleImageUploadRequest,
  emailRequestStatus,
  isEmailAccountDeleted = false,
}) => {
  // ============================================================================
  // STATE VARIABLES
  // ============================================================================
  const editorRef = useRef<any>(null);

  // Draft management state
  const [isDraftSavedTextVisible, setIsDraftSavedTextVisible] = useState(false);
  const [isDiscardModalVisible, setIsDiscardModalVisible] = useState(false);
  const [selectedEmailAccountId, setSelectedEmailAccountId] = useState<
    number | null
  >(null);
  const [isDraftApiSucceed, setIsDraftApiSucceed] = useState(true);

  // Template management state
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [isTemplateApplied, setIsTemplateApplied] = useState(false);
  const [isFromSelected, setIsFromSelected] = useState<string[] | null>(null);

  // Form values state
  const [values, setValues] = useState<StateValues>(
    defaultDraftEditorFields || editorDefaultFields,
  );

  // Draft data states - previous (API response) and current (new/updated)
  const draftData = {
    content: defaultDraftEditorFields?.content,
    to: defaultDraftEditorFields?.to,
    cc: defaultDraftEditorFields?.cc,
    bcc: defaultDraftEditorFields?.bcc,
    subject: defaultDraftEditorFields?.subject,
    from: defaultDraftEditorFields?.from,
    attachments: defaultDraftEditorFields?.attachments ?? [],
  };
  const [
    previousDraftData,
    setPreviousDraftData,
  ] = useState<StateValues | null>(draftData);
  const [currentDraftData, setCurrentDraftData] = useState<StateValues | null>(
    null,
  );

  // Form errors state
  const [errors, setErrors] = useState<StateErrors>({
    from: '',
    to: '',
    cc: '',
    bcc: '',
    content: '',
    subject: '',
  });

  // Temporary input values (for email input fields)
  const [temporaryInputValues, setTemporaryInputValues] = useState({
    from: '',
    to: '',
    cc: '',
    bcc: '',
  });

  // CC/BCC visibility state
  const [isCCInputVisible, setIsCCInputVisible] = useState(
    editorDefaultFields?.cc?.length > 0 ||
      defaultDraftEditorFields?.cc?.length > 0,
  );
  const [isBCCInputVisible, setIsBCCInputVisible] = useState(
    editorDefaultFields?.bcc?.length > 0 ||
      defaultDraftEditorFields?.bcc?.length > 0,
  );

  // ============================================================================
  // COMPUTED VALUES & MEMOIZED DATA
  // ============================================================================

  // Prepare merge tag options for subject field
  const subjectMergeTagOptions = useMemo(
    () => prepareMergeTagOptions(mergeTagOptions || []),
    [mergeTagOptions],
  );

  /**
   * Strips HTML tags from a string
   */
  const stripHtmlFromSubject = useCallback(
    (value?: string) =>
      value
        ? value
            .replace(/<\/?[a-zA-Z][^>]*>/g, '')
            .replace(/\u00A0/g, ' ')
            .trim()
        : '',
    [],
  );

  // Helper function to prepare draft data from current values
  const prepareDraftData = useCallback((): StateValues | null => {
    // const defaultSelectedFromEmailIds = editorDefaultFields?.from
    //   ? (Array.isArray(editorDefaultFields?.from)
    //       ? editorDefaultFields?.from
    //       : []
    //     )?.map((item) => item?.id)
    //   : [];

    // Prepare attachments payload
    const draftAttachments =
      attachments
        ?.filter(
          (item) => item?.status === AttachmentUploadStatus.UploadSuccess,
        )
        ?.map(
          (item): UnifiedScheduleAttachment => ({
            id: item?.attachmentId,
            name: item?.file?.name,
            size: String(item?.file?.size),
            type: item?.file?.type,
          }),
        ) || [];

    // Use inlineEditorInputValue as fallback to ensure subject is included in draft
    // This matches the input field's display logic: values?.subject || inlineEditorInputValue || ''
    const subjectValue = values?.subject || inlineEditorInputValue || '';

    return {
      content: values.content || '',
      to: values.to || [],
      cc: values.cc || [],
      bcc: values.bcc || [],
      subject: subjectValue,
      from: values.from || [],
      attachments: draftAttachments,
    };
  }, [
    values,
    attachments,
    editorDefaultFields,
    defaultDraftEditorFields,
    inlineEditorInputValue,
    stripHtmlFromSubject,
  ]);

  // Helper function to compare draft data
  const compareDraftData = useCallback(
    (previous: StateValues | null, current: StateValues | null): boolean => {
      if (!previous || !current) return true;

      return (
        previous.content !== current.content ||
        JSON.stringify(previous?.to) !== JSON.stringify(current.to) ||
        JSON.stringify(previous?.cc) !== JSON.stringify(current.cc) ||
        JSON.stringify(previous?.bcc) !== JSON.stringify(current.bcc) ||
        previous?.subject !== current?.subject ||
        JSON.stringify(previous?.from) !== JSON.stringify(current?.from) ||
        JSON.stringify(previous?.attachments) !==
          JSON.stringify(current?.attachments)
      );
    },
    [],
  );

  // Debounced draft save function - compares previous and current draft data
  const debouncedDraftSave = useMemo(
    () =>
      debounce(() => {
        if (!draftProspectEmailRequest || !selectedProspectId) return;

        // Don't send if request is already pending
        if (draftProspectEmailRequestStatus === RequestStatus.Pending) {
          return;
        }

        // Check if data has changed
        const hasChanges = compareDraftData(
          previousDraftData,
          currentDraftData,
        );

        if (!hasChanges || !currentDraftData) return;

        // Check if we have required fields
        const hasRequiredFields =
          currentDraftData?.content?.length > 0 &&
          currentDraftData?.subject?.length > 0 &&
          currentDraftData?.to?.length > 0 &&
          (action === 'reply' || currentDraftData?.from?.length > 0);

        if (!hasRequiredFields) return;

        const defaultSelectedFromEmailIds = editorDefaultFields?.from
          ? (Array.isArray(editorDefaultFields?.from)
              ? editorDefaultFields?.from
              : []
            )?.map((item) => item?.id)
          : [];
        // Prepare draft payload
        const replyDraftPayload: ReplyDraftPayload = {
          ...(defaultDraftEditorFields?.unifiedScheduledId
            ? {
                unifiedScheduledId:
                  defaultDraftEditorFields?.unifiedScheduledId,
              }
            : {}),
          ...(action === 'reply' ? {} : { isFirstEmailDraft: true }),
          ...(action === 'reply' ? {} : { prospectId: selectedProspectId }),
          ...(action === 'reply'
            ? {}
            : {
                emailAccountId:
                  selectedEmailAccountId || defaultSelectedFromEmailIds[0],
              }),
          emailThreadId: defaultDraftEditorFields?.emailThreadId,
          content: currentDraftData.content,
          templateId: 0,
          ...(currentDraftData.to.length > 0
            ? { to: currentDraftData.to }
            : {}),
          cc: currentDraftData.cc,
          bcc: currentDraftData.bcc,
          attachments: currentDraftData.attachments,
          subject: stripHtmlFromSubject(
            stripChipTagsFromHtml(currentDraftData.subject),
          ),
          ...(defaultDraftEditorFields
            ? {
                repliedOnEmailId: Math.abs(
                  Number(defaultDraftEditorFields?.emailId),
                ),
              }
            : {}),
        };
        setPreviousDraftData(currentDraftData);
        draftProspectEmailRequest(replyDraftPayload);
      }, 1000),
    [
      draftProspectEmailRequest,
      selectedProspectId,
      draftProspectEmailRequestStatus,
      compareDraftData,
      previousDraftData,
      currentDraftData,
      action,
      values?.emailId,
      editorDefaultFields,
      selectedEmailAccountId,
      defaultDraftEditorFields,
    ],
  );

  // Debounced "Draft Auto Saved" text visibility
  const debouncedDraftSavedText = useMemo(
    () =>
      debounce(() => {
        setIsDraftSavedTextVisible(false);
      }, 2000),
    [],
  );

  // ============================================================================
  // VALIDATION & HELPER FUNCTIONS
  // ============================================================================

  /**
   * Validates form field values
   */
  const validate = useCallback(
    (name: string, value: string | string[]): string => {
      const valueToValidate =
        typeof value === 'string' ? value : value.join(',');
      return rawValidate({
        name,
        value: valueToValidate,
        allowEmptySubject: true,
        showTitle: false,
        attachments,
        t,
        noMergeAndVariableTagsAllowed: values?.to?.length > 1,
        // Consider as external email only when there is no sequenceId
        isExternalEmail: !sequenceId,
      });
    },
    [attachments, sequenceId, t],
  );

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  // Toggle CC/BCC input visibility
  const onCCInputToggle = useCallback(
    () => setIsCCInputVisible((prev) => !prev),
    [],
  );
  const onBCCInputToggle = useCallback(
    () => setIsBCCInputVisible((prev) => !prev),
    [],
  );

  // Handle temporary input value changes (unified handler for all input fields)
  const onTempInputChange = useCallback((name: string, value: string) => {
    setTemporaryInputValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Handle "From" field email changes (dropdown selection)
  const onFromFieldEmailsChange = useCallback(
    (name: string, value: string[], selectedEmailId: number) => {
      // Validate all emails
      const invalidEmail = value.find((v) => validate(name, v) !== '');
      setErrors((prev) => ({
        ...prev,
        [name]: invalidEmail ? validate(name, invalidEmail) : '',
      }));
      setIsFromSelected(value);
      // Update full email list
      setValues((prev) => ({ ...prev, [name]: value }));
      // setDirty(
      //   produce((draft) => {
      //     draft[name] = true;
      //   }),
      // );
      setSelectedEmailAccountId(selectedEmailId);

      // Notify parent with last selected email
      onFromFieldChange?.(value[value.length - 1]);

      // Reset input field
      setTemporaryInputValues((prev) => ({ ...prev, [name]: '' }));
    },
    [onFromFieldChange, validate],
  );

  // Handle email input changes (To, CC, BCC fields)
  const onInputChange = useCallback(
    (name: string, value: string[]) => {
      // Validate all emails
      value.forEach((v) => {
        const error = validate(name, v);
        setErrors((prev) => ({ ...prev, [name]: error }));
      });

      // Update values and mark as dirty
      setValues(
        produce((draft) => {
          draft[name] = value;
        }),
      );
      // setDirty(
      //   produce((draft) => {
      //     draft[name] = true;
      //   }),
      // );

      // If "To" field changes, revalidate content
      if (name === 'to') {
        const contentError = validate('content', values?.content);
        setErrors((prev) => ({ ...prev, content: contentError }));
      }
    },
    [validate, values.content],
  );

  // ============================================================================
  // DRAFT MANAGEMENT
  // ============================================================================

  // Update currentDraftData whenever values or attachments change
  useEffect(() => {
    const newDraftData = prepareDraftData();
    if (newDraftData) {
      setCurrentDraftData(newDraftData);
    }
  }, [
    values.content,
    values.to,
    values.cc,
    values.bcc,
    values.subject,
    values.from,
    attachments,
    inlineEditorInputValue,
    prepareDraftData,
  ]);

  // Initialize previousDraftData from defaultDraftEditorFields or editorDefaultFields
  // useEffect(() => {
  //   const initialDraftData = prepareDraftData();
  //   if (initialDraftData) {
  //     // setPreviousDraftData(initialDraftData);
  //     setCurrentDraftData(initialDraftData);
  //   }
  // }, [
  //   defaultDraftEditorFields?.content,
  //   defaultDraftEditorFields?.subject,
  //   editorDefaultFields?.content,
  //   editorDefaultFields?.subject,
  // ]);

  // Trigger debounced draft save when currentDraftData changes
  useEffect(() => {
    if (currentDraftData) {
      debouncedDraftSave();
    }
  }, [currentDraftData, debouncedDraftSave]);

  // Handle editor content changes
  const onEditorChange = useCallback(
    (value: string) => {
      const error = validate('content', value);
      setErrors((prev) => ({ ...prev, content: error }));
      setValues(
        produce((draft) => {
          draft.content = value;
        }),
      );
    },
    [validate],
  );

  useEffect(() => {
    executeOnRequestStatus({
      status: draftProspectEmailRequestStatus,
      onSuccess: () => {
        resetReplyForwardDraftRequests(defaultDraftEditorFields?.emailId);
        setIsDraftApiSucceed(false);
      },
      onFailed: () => {
        resetReplyForwardDraftRequests(defaultDraftEditorFields?.emailId);
      },
    });
  }, [draftProspectEmailRequestStatus]);

  // Get editor instance reference
  const getEditorInstance = useCallback((editor: any) => {
    editorRef.current = editor;
  }, []);

  // Handle draft request status changes
  useEffect(() => {
    if (draftProspectEmailRequestStatus === RequestStatus.Succeeded) {
      // Update previousDraftData with currentDraftData when draft is saved successfully
      // if (currentDraftData) {
      //   setPreviousDraftData(currentDraftData);
      // }
      setIsDraftSavedTextVisible(true);
      debouncedDraftSavedText();
    }
  }, [
    draftProspectEmailRequestStatus,
    currentDraftData,
    debouncedDraftSavedText,
  ]);

  // Cleanup debounced functions on unmount
  useEffect(
    () => () => {
      debouncedDraftSave.cancel();
      debouncedDraftSavedText.cancel();
    },
    [debouncedDraftSave, debouncedDraftSavedText],
  );

  // Handle send email
  const handleSend = useCallback(() => {
    const hasErrors =
      !!errors.to ||
      !!errors.cc ||
      !!errors.bcc ||
      !!errors.content ||
      !!errors.subject ||
      values.to.length === 0;

    if (hasErrors) return;

    const derivedFromFieldValue =
      values.from.length > 0
        ? values.from[values.from.length - 1]
        : selectedEmail?.fromEmail || '';

    // Strip chip tags from HTML before sending
    const convertedContent = stripChipTagsFromHtml(values.content);

    onSend?.({
      to: values.to,
      cc: values.cc,
      bcc: values.bcc,
      subject: stripHtmlFromSubject(
        stripChipTagsFromHtml(values?.subject || ''),
      ),
      from: derivedFromFieldValue,
      content: convertedContent,
      attachments: attachments.filter(
        (item) => item.status === AttachmentUploadStatus.UploadSuccess,
      ),
      emailThreadId: defaultDraftEditorFields?.emailThreadId,
      emailId: values.emailId,
      templateId: selectedTemplate ? Number(selectedTemplate.id) : undefined,
    });
  }, [
    errors,
    values,
    selectedEmail,
    attachments,
    onSend,
    stripHtmlFromSubject,
    defaultDraftEditorFields?.emailThreadId,
    selectedTemplate,
  ]);

  // Handle discard email
  const handleDiscard = useCallback(() => {
    if (defaultDraftEditorFields) {
      setSelectedRepliedEmailId(
        Math.abs(Number(defaultDraftEditorFields?.emailId)),
      );
      sendReplyDraftDiscardRequest?.({
        repliedOnEmailId: Math.abs(Number(defaultDraftEditorFields?.emailId)),
        hashId: values?.hashId,
        isDraftTab: false,
      });
    }

    onDiscard?.();
    setIsDiscardModalVisible(false);
  }, [
    onDiscard,
    values?.hashId,
    values?.emailId,
    sendReplyDraftDiscardRequest,
    defaultDraftEditorFields,
  ]);

  const handleAttachmentsChange = (
    updatedAttachments: AttachmentType[],
  ): void => {
    setAttachments(updatedAttachments);
  };

  const handleAttachmentStatusChange = (updatedAttachment: AttachmentType) => {
    setAttachments((prevAttachments) =>
      prevAttachments.map((attachment) =>
        attachment.attachmentIdentifier ===
        updatedAttachment.attachmentIdentifier
          ? {
              ...attachment,
              ...updatedAttachment,
              status:
                updatedAttachment.status ||
                AttachmentUploadStatus.UploadSuccess,
            }
          : attachment,
      ),
    );
  };

  // useEffect(() => {
  //   executeOnRequestStatus({
  //     status: resetEmailDraftDataRequestStatus,
  //     onSuccess: () => {
  //       resetReplyForwardDraftRequests(defaultDraftEditorFields?.emailId);
  //       setValues(null);
  //     },
  //   });
  // }, [resetEmailDraftDataRequestStatus, defaultDraftEditorFields?.emailId]);

  useEffect(() => {
    executeOnRequestStatus({
      status: emailRequestStatus,
      onSuccess: () => {
        toaster.success('Email sent successfully', { theme: Theme.New });
      },
      // onFailed: () => {
      //   toaster.error('Failed to send email', { theme: Theme.New });
      // },
    });
  }, [emailRequestStatus, onDiscard]);

  // Initialize attachments from defaultDraftEditorFields when available
  useEffect(() => {
    if (
      defaultDraftEditorFields?.attachments &&
      defaultDraftEditorFields?.attachments.length > 0 &&
      setAttachments &&
      (!attachments || attachments?.length === 0)
    ) {
      // Convert UnifiedScheduleAttachment[] to AttachmentType[] format
      const formattedAttachments: AttachmentType[] = defaultDraftEditorFields.attachments.map(
        (item: UnifiedScheduleAttachment): AttachmentType => ({
          attachmentId: item.id,
          status: AttachmentUploadStatus.UploadSuccess,
          attachmentSize: Number(item.size),
          attachmentIdentifier: generateRandomString(6),
          file: {
            name: item.name,
            size: item.size,
            type: item.type,
          },
        }),
      );
      setAttachments(formattedAttachments);
    }
  }, [defaultDraftEditorFields?.attachments, setAttachments, attachments]);

  // Check if send button should be disabled

  const subjectValue = values?.subject || inlineEditorInputValue || '';

  const isSendDisabled =
    (action !== 'reply' && !!errors.from) ||
    !!errors.to ||
    !!errors.cc ||
    !!errors.bcc ||
    !!errors.content ||
    !!errors.subject ||
    values.to.length === 0 ||
    subjectValue === '' ||
    values?.content === '' ||
    (action !== 'reply' && values?.from?.length === 0);

  // Handle merge tag insertion in content
  const handleAddMergeTagInContent = useCallback((option: any) => {
    if (!option?.tag) return;

    const { tag } = option;
    const uuid = uuidv4();

    let formattedTag = tag;
    const lastPipeIndex = tag.lastIndexOf('|');

    if (lastPipeIndex !== -1) {
      const beforePipe = tag.slice(0, lastPipeIndex + 1);
      const afterPipe = tag.slice(lastPipeIndex + 1);

      formattedTag =
        beforePipe + (isValidHTML(afterPipe) ? afterPipe : nl2br(afterPipe));
    }

    editorRef.current?.insertContent(
      insertChipForMergeTag(formattedTag, uuid, false, true, option?.fieldType),
    );
    setCaretAfterChip(editorRef.current, uuid);
  }, []);

  // Handle variable tag insertion in content
  const handleAddVariableTagInContent = useCallback((tag: string) => {
    if (!tag) return;

    const uuid = uuidv4();
    if (dateChipTags(tag)) {
      editorRef.current?.insertContent(insertChipForDateTag(tag, uuid, true));
    } else {
      editorRef.current?.insertContent(insertChipForMergeTag(tag, uuid, true));
    }
    // Move caret to after the inserted chip
    setCaretAfterChip(editorRef.current, uuid);
  }, []);

  // Handle template modal show/hide
  const showTemplateModal = useCallback(() => {
    setShowTemplates(true);
  }, []);

  const hideTemplateModal = useCallback(() => {
    setShowTemplates(false);
  }, []);

  // Handle template change
  const onTemplateChange = useCallback(
    (selectedTemplate: any) => {
      const templateContent = selectedTemplate.content;

      // Insert template content into editor
      editorRef.current?.execCommand?.(
        'mceInsertContent',
        false,
        templateContent,
      );

      // Update content value
      const updatedContent =
        editorRef.current?.getContent?.() || values.content;

      // Add template attachments if any
      if (selectedTemplate?.template?.templateAttachments) {
        const templateAttachments = getFormattedAttachmentsData(
          selectedTemplate.template.templateAttachments,
          AttachmentUploadStatus.UploadSuccess,
        );
        setAttachments?.((prevAttachments) => [
          ...prevAttachments,
          ...templateAttachments,
        ]);
      }

      // Update state
      setValues((prev) => ({
        ...prev,
        content: updatedContent,
      }));
      setSelectedTemplate(selectedTemplate);
      setIsTemplateApplied(true);
      setShowTemplates(false);
    },
    [values.content, setAttachments],
  );

  // Handle template applied reset
  const onTemplateAppliedReset = useCallback(() => {
    setIsTemplateApplied(false);
  }, []);

  return (
    <div
      className={`custom-reply-forward-editor ${className}`}
      // ${[undefined, 'Edit Draft']?.includes(label)
      //   ? 'custom-reply-forward-draft-editor'
      //   : ''}
    >
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center">
        <div className="custom-reply-forward-editor--label">
          {defaultDraftEditorFields?.content !== '' &&
          defaultDraftEditorFields !== null ? (
            <span className="custom-reply-forward-editor--label-draft">
              Draft
            </span>
          ) : (
            <span>{label || 'Compose email'}</span>
          )}
          {isDraftSavedTextVisible && <span>Draft Auto Saved</span>}
        </div>
      </div>

      {/* Form Container */}
      <div className="custom-reply-forward-editor--container">
        {/* From Field */}
        {showFromField && (
          <EmailDropdownInput
            name="from"
            label="From:"
            value={temporaryInputValues.from}
            emails={isFromSelected ?? values?.from}
            onChange={onFromFieldEmailsChange}
            onValueChange={onTempInputChange}
            isEmailsRemovable={true}
            options={fromAddresses}
            getEmailAccounts={getEmailAccounts}
            emailAccount={emailAccount}
            nonRemovableEmails={
              Array.isArray(editorDefaultFields?.from)
                ? editorDefaultFields?.from?.map((email) => email?.email)
                : []
            }
          />
        )}
        <EmailInput
          name="to"
          label="To:"
          value={temporaryInputValues.to}
          emails={values.to}
          onChange={onInputChange}
          onValueChange={onTempInputChange}
          isEmailsRemovable={true}
          showCCInput
          showBCCInput
          isCCInputVisible={isCCInputVisible}
          isBCCInputVisible={isBCCInputVisible}
          onCCInputToggle={onCCInputToggle}
          onBCCInputToggle={onBCCInputToggle}
          nonRemovableEmails={editorDefaultFields?.to || []}
        />
        {isCCInputVisible && (
          <EmailInput
            name="cc"
            label="Cc:"
            value={temporaryInputValues.cc}
            emails={values.cc}
            onChange={onInputChange}
            onValueChange={onTempInputChange}
            isEmailsRemovable={true}
          />
        )}
        {isBCCInputVisible && (
          <EmailInput
            name="bcc"
            label="Bcc:"
            value={temporaryInputValues.bcc}
            emails={values.bcc}
            onChange={onInputChange}
            onValueChange={onTempInputChange}
            isEmailsRemovable={true}
          />
        )}

        <SingleEditorInput
          inputValue={values?.subject || inlineEditorInputValue || ''}
          onInputChange={(value) => {
            // Update values.subject (React batches both updates together)
            setValues((prev) => ({ ...prev, subject: value }));
            // Also update inlineEditorInputValue for parent component sync
            // React batches these updates, so no double render occurs
            setInlineEditorInputValue?.(value);
            // Validate subject
            const error = validate('subject', value);
            setErrors((prev) => ({ ...prev, subject: error }));
          }}
          onInputBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            const { value } = e.target;
            if (value) {
              setErrors((prev) => ({
                ...prev,
                subject: '',
              }));
              return;
            }
            setErrors((prev) => ({
              ...prev,
              subject:
                'Subject line can’t be empty if sending email as new thread',
            }));
          }}
          enableEmoji
          enableMergeTag={false}
          enableSpintax={false}
          // mergeTagOptions={subjectMergeTagOptions}
          placeholder="Add a subject"
          showDivider={false}
          showTitle="Subject:"
          disableEditMode={action === 'reply'}
        />
        {errors.subject && (
          <div className="email-modal-input-subject-error-wrapper">
            <div className="email-modal-input-error">
              <img
                src={Images.AlertTriangleRed}
                alt="Alert"
                width={16}
                height={16}
              />
              {errors.subject}
            </div>
          </div>
        )}
        <div className="custom-reply-forward-editor--editor-wrapper">
          <Editor
            ref={editorRef}
            placeholder="Write your message here"
            getEditorInstance={getEditorInstance}
            value={values.content}
            onEditorChange={onEditorChange}
            error={!!errors.content}
            errorMessage={errors.content}
            imageUploader={
              handleImageUploadRequest
                ? ({ formData, onUploadProgress }) =>
                    handleImageUploadRequest(formData, onUploadProgress)
                : undefined
            }
            shouldRenderTemplate={!!sequenceId}
            showTemplateModal={sequenceId && showTemplateModal}
            showAddAttachments={true}
            attachments={attachments}
            onAttachmentStatusChange={handleAttachmentStatusChange}
            onAttachmentsChange={handleAttachmentsChange}
            sendAttachmentUploadRequest={handleSendAttachmentUploadRequest}
            uploadAttachmentRequestStatus={uploadAttachmentRequestStatus}
            uploadAttachmentRequestError={uploadAttachmentRequestError}
            uploadAttachmentRequestMessage={uploadAttachmentRequestMessage}
            uploadedAttachment={uploadedAttachment}
            sendDeleteAttachmentRequest={handleSendDeleteAttachmentRequest}
            deleteAttachmentRequestStatus={deleteAttachmentRequestStatus}
            deleteAttachmentRequestError={deleteAttachmentRequestError}
            deletedAttachmentId={deletedAttachmentId}
            resetUploadAttachmentRequest={handleResetUploadAttachmentRequest}
            resetDeleteAttachmentRequest={handleResetDeleteAttachmentRequest}
            onAttachmentsMetaChange={() => {}}
            showMergeAndVariableTags={!!sequenceId}
            tagsDropdownClassName="custom-reply-forward-editor-tags-dropdown"
            attachmentsClassname="custom-reply-forward-editor-attachment"
            uniqueId={
              selectedEmail?.emailId || defaultDraftEditorFields?.emailId
            }
            action={action}
            onTemplateApplied={isTemplateApplied}
            onTemplateAppliedReset={onTemplateAppliedReset}
          />
          <TemplateModal
            show={showTemplates}
            onClose={hideTemplateModal}
            onTemplateChange={onTemplateChange}
          />
        </div>

        {/* Footer Actions */}
        <div className="custom-reply-forward-editor--footer">
          {onDiscard && (
            <Button
              variant="tertiary"
              {...accessibleOnClick(() => setIsDiscardModalVisible(true))}
              disabled={
                getIsRequestPending(sendEmailProspectTabStatus) ||
                getIsRequestPending(replyEmailRequestStatus)
              }
            >
              Discard
            </Button>
          )}

          {onSend &&
            (() => {
              const sendBtn = (
                <Button
                  onClick={handleSend}
                  disabled={
                    isSendDisabled ||
                    getIsRequestPending(sendEmailProspectTabStatus) ||
                    getIsRequestPending(replyEmailRequestStatus) ||
                    (values?.content === '' && isDraftApiSucceed) ||
                    !defaultDraftEditorFields ||
                    isEmailAccountDeleted
                  }
                >
                  Send
                </Button>
              );
              return isEmailAccountDeleted ? (
                <OverlayTooltip
                  text="Can't Send, this email account has been deleted."
                  placement={Placement.Top}
                >
                  {sendBtn}
                </OverlayTooltip>
              ) : (
                sendBtn
              );
            })()}
        </div>
      </div>

      {/* Discard Confirmation Modal */}
      <DiscardEmailChangesModal
        show={isDiscardModalVisible}
        onClose={() => setIsDiscardModalVisible(false)}
        onHide={handleDiscard}
      />
    </div>
  );
};

export default withTranslation()(CustomReplyForwardEditor);
